import React, { PropsWithChildren, ReactNode } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { FONT_SIZES, FONT_WEIGHTS, FontSize } from '../../fundamentals/typography/typography';
import { Span } from '../../components/Texts/Texts';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { Delta, IDeltaProps } from '../../components/Delta/Delta';
import { DotIcon, DotIconVariant } from '../../components/DotIcon/DotIcon';
import { IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { ISkeletonProps, Skeleton } from '../../components/Skeleton/Skeleton';
import { getHexOpacity } from '../../common/utils';
import { INumericProps, Numeric } from '../../fundamentals/numeric/Numeric';
import { GapContainer } from '../../fundamentals/spaces/SpaceContainer';
import { PX_TO_REM, SIZES } from '../../fundamentals/sizes/sizes';
import { IconButton } from '../../components/IconButton/IconButton';
import { IGutterAsMarginProps } from '../../fundamentals/spaces/space';
import { TRADING_VARIANTS, TradingVariant } from '../../fundamentals/colours/variants';

const DEFAULT_SKELETON_WIDTH = 150;
type CustomLineHeight = 10 | 12;

export type MetricSize = typeof SIZES.LARGE | typeof SIZES.SMALL;

export const metricColours = ({ trading, base }: IColoursCollection) => ({
  metricColours: {
    text: trading.unchanged,
    placeholder: `${base.black.colour}${getHexOpacity(60)}`,
    light: {
      text: base.white.colour,
      placeholder: `${base.white.colour}${getHexOpacity(60)}`
    },
    [TRADING_VARIANTS.POSITIVE]: {
      text: trading.positive
    },
    [TRADING_VARIANTS.NEGATIVE]: {
      text: trading.negative
    },
    [TRADING_VARIANTS.UNCHANGED]: {
      text: trading.unchanged
    }
  }
});

export interface IMetricProps extends IGutterAsMarginProps {
  value?: INumericProps;
  label?: string;
  size?: MetricSize;
  variant?: TradingVariant;
  light?: boolean;
  inline?: boolean;
  labelDotVariant?: DotIconVariant;
  trailingDotVariant?: DotIconVariant;
  delta?: Omit<IDeltaProps, 'type' | 'size' | 'reverse' | 'footer' | 'noWrap' | 'animate'>;
  customTool?: ReactNode;
  skeleton?: Omit<ISkeletonProps, 'loaderType'>;
  onRefresh?: () => void;
}

const MetricSizeToFontSize: Record<MetricSize, { labelInline: FontSize; labelStacked: FontSize; value: FontSize }> = {
  [SIZES.SMALL]: { labelStacked: FONT_SIZES.TEXT_NANO, labelInline: FONT_SIZES.TEXT_SMALL, value: FONT_SIZES.TEXT_SMALL },
  [SIZES.LARGE]: { labelStacked: FONT_SIZES.TEXT_MICRO, labelInline: FONT_SIZES.TEXT_LARGE, value: FONT_SIZES.TEXT_LARGE }
};

const MetricSizeToLineHeight: Record<MetricSize, CustomLineHeight> = {
  [SIZES.SMALL]: 10,
  [SIZES.LARGE]: 12
};

export const appendColon = (value: string): string => `${value}${value.endsWith(':') ? '' : ':'}`;

interface IMetricLabelProps extends Required<Pick<IMetricProps, 'inline' | 'light' | 'size' | 'variant'>>, Pick<IMetricProps, 'labelDotVariant'> {
  isLabel?: boolean;
}

const ColourSpan = styled(Span)<{ textColour: string; customLineHeight?: CustomLineHeight; uppercase: boolean }>`
  color: ${({ textColour }) => textColour};
  white-space: nowrap;
  ${({ uppercase }) =>
    uppercase &&
    css`
      text-transform: uppercase;
    `}
  ${({ customLineHeight }) =>
    !!customLineHeight &&
    css`
      && {
        line-height: ${PX_TO_REM[customLineHeight]};
      }
    `};
`;

const Container = styled(GapContainer)<{ showCustomGap: boolean }>`
  gap: ${({ showCustomGap }) => PX_TO_REM[showCustomGap ? 6 : 0]};
`;

const MetricText: React.FC<PropsWithChildren<IMetricLabelProps>> = ({ isLabel = false, variant, inline, light, size, children, labelDotVariant, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'MetricLabel');
  const theme = useTheme();
  if (!children) return <></>;

  const fontSize = isLabel ? (inline ? MetricSizeToFontSize[size].labelInline : MetricSizeToFontSize[size].labelStacked) : MetricSizeToFontSize[size].value;
  const labelColourSet = light ? theme.metricColours.light : theme.metricColours;
  const valueColour = theme.metricColours[variant];
  const colour = isLabel ? labelColourSet.placeholder : valueColour.text;
  const fontWeight = size === SIZES.LARGE || (isLabel && !inline) ? FONT_WEIGHTS.SEMI_BOLD : FONT_WEIGHTS.MEDIUM;
  const lineHeight = inline || !isLabel ? undefined : MetricSizeToLineHeight[size];

  return (
    <Container alignItems='center' showCustomGap>
      <ColourSpan
        textColour={colour}
        textSize={fontSize}
        fontWeightCss={fontWeight}
        customLineHeight={lineHeight}
        uppercase={isLabel && !inline}
        {...generateTestId()}
      >
        {children}
      </ColourSpan>
      {labelDotVariant && <DotIcon size={SIZES.XX_MICRO} variant={labelDotVariant} />}
    </Container>
  );
};

export const Metric: React.FC<IMetricProps> = ({
  label,
  inline = false,
  light = false,
  variant = TRADING_VARIANTS.UNCHANGED,
  size = SIZES.LARGE,
  value,
  delta,
  trailingDotVariant,
  labelDotVariant,
  onRefresh,
  customTool,
  skeleton,
  ...rest
}) => {
  const { generateTestId } = useTestId(rest, 'Metric');

  const textProps = {
    inline,
    light,
    size,
    variant
  };

  const labelProps = {
    ...textProps,
    isLabel: true,
    labelDotVariant
  };

  const labelText = label && inline ? appendColon(label) : label;

  return (
    <Container column={!inline} alignItems={inline ? 'center' : 'start'} showCustomGap={inline} gutter={false} {...generateTestId()} {...rest}>
      <MetricText {...labelProps} {...generateTestId('Label')}>
        {labelText}
      </MetricText>
      {value?.value === undefined ? (
        <Skeleton
          skeletonWidth={skeleton?.skeletonWidth ?? DEFAULT_SKELETON_WIDTH}
          light={skeleton?.light ?? false}
          gutterTop={inline ? false : 4}
          loaderType='singleLine'
          {...generateTestId('Skeleton')}
        />
      ) : (
        <Container column={false} showCustomGap {...generateTestId()} alignItems='center'>
          <MetricText {...textProps} {...generateTestId('Value')}>
            <Numeric {...value} />
          </MetricText>
          {delta && <Delta gutter={false} {...delta} type={'textColoured'} deltaSize={SIZES.XX_SMALL} showIcon {...generateTestId('Delta')} />}
          {customTool}
          {trailingDotVariant && <DotIcon size={SIZES.XX_MICRO} variant={trailingDotVariant} {...generateTestId('TrailingDot')} />}
          {onRefresh && (
            <IconButton
              iconType='round'
              icon='refresh'
              variant={light ? 'linkWhite' : 'linkDark'}
              size='xxSmall'
              onClick={onRefresh}
              {...generateTestId('Refresh')}
            />
          )}
        </Container>
      )}
    </Container>
  );
};
